import { Button, Dialog, Input } from '@artosera/ui';
import { TYPES_CONTACT } from '@artosera/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCreerTypeContact,
  useRenommerTypeContact,
  useSupprimerTypeContact,
  useTypesContactPerso,
  type TypeContactPersoPresente,
} from '../../api/typesContact.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { useErreurFormulaire } from '../useErreurFormulaire.js';
import styles from './LieuxPage.module.css';

/** Réglages › Types de contact (ARTO-199) : les six fixes, puis ceux de la galerie. */
export function TypesContactPage(): JSX.Element {
  const { t } = useTranslation(['regie', 'commun', 'contacts']);
  const { galerieSlug, galerie } = useShellContext();
  const peutEcrire = galerieALaction(galerie, 'settings:write');
  const types = useTypesContactPerso(galerieSlug);
  const creer = useCreerTypeContact(galerieSlug);
  const renommer = useRenommerTypeContact(galerieSlug);
  const supprimer = useSupprimerTypeContact(galerieSlug);

  const [nouveau, setNouveau] = useState('');
  const [aRenommer, setARenommer] = useState<TypeContactPersoPresente | null>(null);
  const [libelle, setLibelle] = useState('');
  const [aSupprimer, setASupprimer] = useState<TypeContactPersoPresente | null>(null);
  const formulaire = useErreurFormulaire();

  async function ajouter(): Promise<void> {
    formulaire.reinitialiser();
    try {
      await creer.mutateAsync(nouveau.trim());
      setNouveau('');
    } catch (error) {
      formulaire.appliquer(error, ['libelle']);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.entete}>
        <div>
          <h1 className={styles.titre}>{t('regie:reglages.typesContact.titre')}</h1>
          <p className={styles.sousTitre}>{t('regie:reglages.typesContact.sousTitre')}</p>
        </div>
      </div>

      <h2>{t('regie:reglages.typesContact.fixes')}</h2>
      <p>{TYPES_CONTACT.map((code) => t(`contacts:types.${code}`)).join(' · ')}</p>

      <h2>{t('regie:reglages.typesContact.personnalises')}</h2>
      {types.data?.length === 0 ? (
        <p className={styles.vide}>{t('regie:reglages.typesContact.aucun')}</p>
      ) : (
        <div className={styles.cadre}>
          <table className={styles.tableau}>
            <tbody>
              {types.data?.map((type) => (
                <tr key={type.code}>
                  <td className={styles.celluleNom}>{type.libelle}</td>
                  <td>{t('regie:reglages.typesContact.contacts', { count: type.contacts })}</td>
                  <td className={styles.celluleAction}>
                    {peutEcrire ? (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setLibelle(type.libelle);
                            setARenommer(type);
                          }}
                        >
                          {t('regie:reglages.typesContact.renommer')}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setASupprimer(type)}>
                          {t('regie:reglages.typesContact.supprimer')}
                        </Button>
                      </>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {peutEcrire ? (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (nouveau.trim()) void ajouter();
          }}
        >
          <Input
            aria-label={t('regie:reglages.typesContact.nomChamp')}
            placeholder={t('regie:reglages.typesContact.nomChamp')}
            maxLength={60}
            value={nouveau}
            onChange={(event) => setNouveau(event.target.value)}
          />
          <Button type="submit" loading={creer.isPending} disabled={!nouveau.trim()}>
            {t('regie:reglages.typesContact.ajouter')}
          </Button>
          {formulaire.erreur ? (
            <p className={styles.erreur} role="alert">
              {formulaire.erreur}
            </p>
          ) : null}
        </form>
      ) : null}

      <Dialog
        open={aRenommer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setARenommer(null);
        }}
        title={t('regie:reglages.typesContact.renommer')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setARenommer(null)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              loading={renommer.isPending}
              disabled={!libelle.trim()}
              onClick={() => {
                if (aRenommer)
                  renommer.mutate(
                    { code: aRenommer.code, libelle: libelle.trim() },
                    { onSuccess: () => setARenommer(null) },
                  );
              }}
            >
              {t('regie:reglages.typesContact.enregistrer')}
            </Button>
          </>
        }
      >
        <Input
          aria-label={t('regie:reglages.typesContact.nomChamp')}
          maxLength={60}
          value={libelle}
          onChange={(event) => setLibelle(event.target.value)}
        />
      </Dialog>

      <Dialog
        open={aSupprimer !== null}
        onOpenChange={(ouvert) => {
          if (!ouvert) setASupprimer(null);
        }}
        title={t('regie:reglages.typesContact.supprimerTitre')}
        description={t('regie:reglages.typesContact.supprimerTexte')}
        closeLabel={t('commun:actions.fermer')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setASupprimer(null)}>
              {t('commun:actions.annuler')}
            </Button>
            <Button
              variant="danger"
              loading={supprimer.isPending}
              onClick={() => {
                if (aSupprimer)
                  supprimer.mutate(aSupprimer.code, { onSuccess: () => setASupprimer(null) });
              }}
            >
              {t('regie:reglages.typesContact.supprimer')}
            </Button>
          </>
        }
      >
        <p>{aSupprimer?.libelle}</p>
      </Dialog>
    </div>
  );
}
